/**
 * Theme context provider for managing user theme preferences.
 */
import React, { createContext, useContext, useEffect, useState } from 'react';
import { getAuthUser } from '../host/utils/auth';
import { preferenceService } from '../services/preferenceService';

const ThemeContext = createContext({
  theme: 'light',
  setTheme: () => {},
  isLoading: false,
  error: null,
  systemTheme: 'light'
});

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState('light');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [systemTheme, setSystemTheme] = useState('light');

  // Detect system theme preference
  useEffect(() => {
    const detectSystemTheme = () => {
      if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        setSystemTheme('dark');
      } else {
        setSystemTheme('light');
      }
    };

    detectSystemTheme();

    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => setSystemTheme(e.matches ? 'dark' : 'light');

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    } else {
      mediaQuery.addListener(handleChange); // Fallback for older browsers
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  // Load theme from server on mount/login
  useEffect(() => {
    const loadTheme = async () => {
      const user = getAuthUser();
      if (!user) {
        // Not logged in, use light theme
        setThemeState('light');
        document.documentElement.setAttribute('data-theme', 'light');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const preferences = await preferenceService.getPreferences();

        let effectiveTheme = preferences.theme || 'light';

        // Handle 'auto' theme
        if (effectiveTheme === 'auto') {
          effectiveTheme = systemTheme;
        }

        setThemeState(preferences.theme || 'light'); // Store the actual preference
        document.documentElement.setAttribute('data-theme', effectiveTheme);

        // Also check for cached theme for instant loading
        const cachedTheme = sessionStorage.getItem(`theme_${user.user_id}`);
        if (cachedTheme && !preferences.theme) {
          // Use cached theme while waiting for server
          document.documentElement.setAttribute('data-theme', cachedTheme);
        }
      } catch (err) {
        console.error('Failed to load theme preferences:', err);
        setError(err.message);

        // Fallback to cached or default
        const user = getAuthUser();
        const cachedTheme = sessionStorage.getItem(`theme_${user?.user_id}`) || 'light';
        setThemeState(cachedTheme);
        document.documentElement.setAttribute('data-theme', cachedTheme);
      } finally {
        setIsLoading(false);
      }
    };

    loadTheme();
  }, [systemTheme]);

  // Update theme
  const setTheme = async (newTheme) => {
    const user = getAuthUser();
    if (!user) {
      console.warn('Cannot set theme: user not authenticated');
      return;
    }

    // Validate theme value
    if (!['light', 'dark', 'auto'].includes(newTheme)) {
      console.error('Invalid theme value:', newTheme);
      return;
    }

    try {
      // Update UI immediately for better UX
      const effectiveTheme = newTheme === 'auto' ? systemTheme : newTheme;
      setThemeState(newTheme);
      document.documentElement.setAttribute('data-theme', effectiveTheme);

      // Cache in sessionStorage for instant loading
      sessionStorage.setItem(`theme_${user.user_id}`, effectiveTheme);

      // Persist to server
      await preferenceService.updatePreferences({ theme: newTheme });

      setError(null);
    } catch (err) {
      console.error('Failed to update theme:', err);
      setError(err.message);

      // Don't revert UI since it might still work locally
      // Just show an error notification if needed
    }
  };

  // Apply effective theme when system theme changes (for 'auto' mode)
  useEffect(() => {
    if (theme === 'auto') {
      document.documentElement.setAttribute('data-theme', systemTheme);

      const user = getAuthUser();
      if (user) {
        sessionStorage.setItem(`theme_${user.user_id}`, systemTheme);
      }
    }
  }, [systemTheme, theme]);

  const value = {
    theme,
    setTheme,
    isLoading,
    error,
    systemTheme,
    effectiveTheme: theme === 'auto' ? systemTheme : theme
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
